import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../services/api';

interface SettingsContextType {
  showTimezoneDebug: boolean;
  setShowTimezoneDebug: (show: boolean) => void;
  timeOffRequestsEnabled: boolean;
  setTimeOffRequestsEnabled: (enabled: boolean) => void;
  clockOutConfirmationEnabled: boolean;
  setClockOutConfirmationEnabled: (enabled: boolean) => void;
  trackingEnabled: boolean;
  setTrackingEnabled: (enabled: boolean) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [showTimezoneDebug, setShowTimezoneDebug] = useState<boolean>(false);
  const [timeOffRequestsEnabled, setTimeOffRequestsEnabled] = useState<boolean>(true);
  const [clockOutConfirmationEnabled, setClockOutConfirmationEnabled] = useState<boolean>(true);
  const [trackingEnabled, setTrackingEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Load global settings on mount
  useEffect(() => {
    const loadGlobalSettings = async () => {
      try {
        // Load timezone debug setting (public endpoint)
        const timezoneResponse = await apiClient.globalSettings.get('timezone_debug_enabled');
        if (timezoneResponse.data.success) {
          setShowTimezoneDebug(timezoneResponse.data.data.value);
        }
      } catch (error) {
        console.error('Failed to load timezone debug setting:', error);
        // Fallback to localStorage
        const savedTimezone = localStorage.getItem('showTimezoneDebug');
        setShowTimezoneDebug(savedTimezone !== null ? JSON.parse(savedTimezone) : false);
      }

      // Load time off requests setting (public endpoint - no auth required)
      try {
        const timeOffResponse = await apiClient.globalSettings.get('time_off_requests_enabled');
        if (timeOffResponse.data.success) {
          setTimeOffRequestsEnabled(timeOffResponse.data.data.value);
        }
      } catch (error) {
        console.error('Failed to load time off requests setting:', error);
        // Fallback to localStorage
        const savedTimeOff = localStorage.getItem('timeOffRequestsEnabled');
        setTimeOffRequestsEnabled(savedTimeOff !== null ? JSON.parse(savedTimeOff) : true);
      }

      // Load clock out confirmation setting (public endpoint - no auth required)
      try {
        const clockOutResponse = await apiClient.globalSettings.get('clock_out_confirmation_enabled');
        if (clockOutResponse.data.success) {
          setClockOutConfirmationEnabled(clockOutResponse.data.data.value);
        }
      } catch (error) {
        console.error('Failed to load clock out confirmation setting:', error);
        // Fallback to localStorage
        const savedClockOut = localStorage.getItem('clockOutConfirmationEnabled');
        setClockOutConfirmationEnabled(savedClockOut !== null ? JSON.parse(savedClockOut) : true);
      }

      // Load tracking enabled setting (public endpoint - no auth required)
      try {
        const trackingResponse = await apiClient.globalSettings.get('tracking_enabled');
        if (trackingResponse.data.success) {
          setTrackingEnabled(trackingResponse.data.data.value);
        }
      } catch (error) {
        console.error('Failed to load tracking enabled setting:', error);
        // Fallback to localStorage
        const savedTracking = localStorage.getItem('trackingEnabled');
        setTrackingEnabled(savedTracking !== null ? JSON.parse(savedTracking) : false);
      }

      setIsLoading(false);
    };

    loadGlobalSettings();
  }, []);

  // Update global setting when changed
  const handleSetShowTimezoneDebug = async (show: boolean) => {
    try {
      await apiClient.globalSettings.update('timezone_debug_enabled', show);
      setShowTimezoneDebug(show);
      // Also update localStorage as fallback
      localStorage.setItem('showTimezoneDebug', JSON.stringify(show));
    } catch (error) {
      console.error('Failed to update global timezone debug setting:', error);
      // Fallback to localStorage
      setShowTimezoneDebug(show);
      localStorage.setItem('showTimezoneDebug', JSON.stringify(show));
    }
  };

  const handleSetTimeOffRequestsEnabled = async (enabled: boolean) => {
    try {
      await apiClient.globalSettings.update('time_off_requests_enabled', enabled);
      setTimeOffRequestsEnabled(enabled);
      // Also update localStorage as fallback
      localStorage.setItem('timeOffRequestsEnabled', JSON.stringify(enabled));
    } catch (error) {
      console.error('Failed to update global time off requests setting:', error);
      // Fallback to localStorage
      setTimeOffRequestsEnabled(enabled);
      localStorage.setItem('timeOffRequestsEnabled', JSON.stringify(enabled));
    }
  };

  const handleSetClockOutConfirmationEnabled = async (enabled: boolean) => {
    try {
      await apiClient.globalSettings.update('clock_out_confirmation_enabled', enabled);
      setClockOutConfirmationEnabled(enabled);
      // Also update localStorage as fallback
      localStorage.setItem('clockOutConfirmationEnabled', JSON.stringify(enabled));
    } catch (error) {
      console.error('Failed to update global clock out confirmation setting:', error);
      // Fallback to localStorage
      setClockOutConfirmationEnabled(enabled);
      localStorage.setItem('clockOutConfirmationEnabled', JSON.stringify(enabled));
    }
  };

  const handleSetTrackingEnabled = async (enabled: boolean) => {
    try {
      await apiClient.globalSettings.update('tracking_enabled', enabled);
      setTrackingEnabled(enabled);
      // Also update localStorage as fallback
      localStorage.setItem('trackingEnabled', JSON.stringify(enabled));
    } catch (error) {
      console.error('Failed to update global tracking enabled setting:', error);
      // Fallback to localStorage
      setTrackingEnabled(enabled);
      localStorage.setItem('trackingEnabled', JSON.stringify(enabled));
    }
  };

  const value: SettingsContextType = {
    showTimezoneDebug,
    setShowTimezoneDebug: handleSetShowTimezoneDebug,
    timeOffRequestsEnabled,
    setTimeOffRequestsEnabled: handleSetTimeOffRequestsEnabled,
    clockOutConfirmationEnabled,
    setClockOutConfirmationEnabled: handleSetClockOutConfirmationEnabled,
    trackingEnabled,
    setTrackingEnabled: handleSetTrackingEnabled,
  };

  // Don't render children until we've loaded the setting
  if (isLoading) {
    return null;
  }

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
